// src/App.js - Modified to include admin routing
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import logger from './utils/logger';
import { MsalAuthenticationTemplate, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionType, InteractionStatus } from '@azure/msal-browser';
import { AuthProvider, useAuth } from './auth/AuthContext';
import EmployeePortal from './components/EmployeePortal';
import RegistrationAndLanding from './components/RegistrationAndLanding';
import EnhancedRegistration from './components/EnhancedRegistration';
import AuthPages from './components/AuthPages';
import EnvironmentSelector from './components/auth/EnvironmentSelector';
import SiriusConnect365Licenses from './components/SiriusConnect365Licenses';
import { loginRequest } from './auth/authConfig';
import ErrorMessage from './components/common/ErrorMessage';
import AuthErrorPage from './components/auth/AuthErrorPage';
import FirstLoginHandler from './components/auth/FirstLoginHandler';
import AuthCallbackPage from './components/auth/AuthCallbackPage';
import DebugPanel from './components/common/DebugPanel';
import AdminRoutes from './components/AdminRoutes';

// Error component for authentication failures
const ErrorComponent = ({ error }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-red-600 mb-4">Authentication Error</h2>
        <p className="text-gray-700">{error.message}</p>
        <button 
          onClick={() => window.location.href = '/'}
          className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Return to Home
        </button>
      </div>
    </div>
  );
};

// Protected route component that checks for authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, authError, activeEnvironment, isPendingFirstLogin, user } = useAuth();
  const { inProgress } = useMsal();
  const navigate = useNavigate();
  const isAdmin = user?.isAdmin === true;

  // Show spinner while AuthContext is loading OR while MSAL is still hydrating
  // accounts from sessionStorage. Without the inProgress check, ProtectedRoute
  // evaluates !isAuthenticated before MSAL has restored the session and incorrectly
  // redirects to /login, causing a loop.
  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // If there's an auth error, show the error page
  if (authError) {
    return <AuthErrorPage message={authError} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If authenticated but no active environment is selected, redirect to environment selector
  if (!activeEnvironment && window.location.pathname !== '/environment-select') {
    return <Navigate to="/environment-select" />;
  }
  
  // If user is admin and trying to access portal, let them through
  // (admin users should be able to access both admin and user portals)
  if (isAdmin && window.location.pathname.startsWith('/portal') && isPendingFirstLogin === false) {
    // Continue to portal, no redirect needed
  }
  
  // If user is admin and not on admin routes or environment select page, redirect to admin dashboard
  // This automatically redirects admin users to the admin portal on login
  if (isAdmin && 
      !window.location.pathname.startsWith('/admin') && 
      !window.location.pathname.startsWith('/portal') && 
      window.location.pathname !== '/environment-select' && 
      !isPendingFirstLogin) {
    return <Navigate to="/admin/dashboard" />;
  }

  // Show first login handler overlay if applicable
  return (
    <>
      {children}
      {isPendingFirstLogin && <FirstLoginHandler />}
    </>
  );
};

// Need to define this wrapper component outside of the main App component
// to properly use Router hooks
const AppRoutes = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [localAuthState, setLocalAuthState] = useState(false);
  const { authError, isLoading: authContextLoading, user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  
  // Add debug logging
  useEffect(() => {
    logger.log("Auth Debug - MSAL state:", {
      accounts: accounts.length,
      isAuthenticated,
      localAuthState,
      isAdmin,
      user: user ? { id: user.id, isAdmin: user.isAdmin } : null
    });
  }, [accounts, isAuthenticated, localAuthState, isAdmin, user]);
  
  // Handle login
  const handleLogin = () => {
    logger.log("Auth Debug - Login handler called");
    setLocalAuthState(true);
    localStorage.setItem('isAuthenticated', 'true');
  };
  
  // Handle logout
  const handleLogout = () => {
    logger.log("Auth Debug - Logout handler called");
    setLocalAuthState(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('activeEnvironment');
    
    // Optional: If you want to ensure a complete logout
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };
  
  // Check for authentication state on mount
  useEffect(() => {
    logger.log("Auth Debug - Checking initial auth state");
    const storedAuthStatus = localStorage.getItem('isAuthenticated');
    
    if (storedAuthStatus === 'true' || accounts.length > 0) {
      logger.log("Auth Debug - Setting local auth state to true");
      setLocalAuthState(true);
    }
  }, [accounts]);
  
  // Handle redirection after authentication
  useEffect(() => {
    const handleRedirect = async () => {
      logger.log("Auth Debug - Handling redirect");
      try {
        // Only handle redirect if MSAL has been initialized
        if (instance) {
          logger.log("Auth Debug - Processing MSAL redirect");
          const result = await instance.handleRedirectPromise();
          if (result) {
            logger.log("Auth Debug - Redirect successful:", result);
            setLocalAuthState(true);
            localStorage.setItem('isAuthenticated', 'true');
          } else {
            logger.log("Auth Debug - No redirect result");
          }
        }
      } catch (error) {
        logger.error("Auth Debug - Error handling redirect:", error);
      }
    };
    
    handleRedirect();
  }, [instance]);
  
  // Loading state
  if (authContextLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="ml-3 text-indigo-600">Authenticating...</p>
      </div>
    );
  }
  
  // If there's an auth error, show the specific error route
  if (authError) {
    return (
      <Routes>
        <Route path="/auth-error" element={<AuthErrorPage message={authError} />} />
        <Route path="*" element={<Navigate to="/auth-error" />} />
      </Routes>
    );
  }
  
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<RegistrationAndLanding />} />
        <Route
          path="/login"
          element={
            (localAuthState || isAuthenticated) && accounts.length > 0 ?
            <Navigate to="/portal" /> :
            <AuthPages
              pageType="signin"
              onLogin={handleLogin}
            />
          }
        />
        <Route path="/forgot-password" element={<Navigate to="/login" />} />
        <Route path="/register" element={<EnhancedRegistration />} />
        <Route path="/pricing" element={<SiriusConnect365Licenses />} />
        <Route path="/auth-error" element={<AuthErrorPage message={authError} />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        
        {/* Environment selection route */}
        <Route
          path="/environment-select"
          element={
            !isAuthenticated && !localAuthState ? 
            <Navigate to="/login" /> :
            <EnvironmentSelector />
          }
        />
        
        {/* Protected routes */}
        <Route 
          path="/portal/*" 
          element={
            !isAuthenticated && !localAuthState ? 
            <Navigate to="/login" /> :
            <ProtectedRoute>
              <EmployeePortal onLogout={handleLogout} />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin/*" 
          element={
            !isAuthenticated && !localAuthState ? 
            <Navigate to="/login" /> :
            <ProtectedRoute>
              <AdminRoutes />
            </ProtectedRoute>
          } 
        />
      </Routes>
      
      {/* Debug panel only shown in development */}
      {process.env.NODE_ENV === 'development' && <DebugPanel />}
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

export default App;